<template>
  <BaseModal
    :is-visible="isVisible"
    title="Sync Module Access"
    subtitle="Update all employees to the current 44-month module access schedule"
    :icon="RefreshCw"
    size="lg"
    type="warning"
    :show-footer="true"
    @close="handleClose"
  >
    <div class="space-y-4">
      <!-- Warning Message -->
      <div class="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl">
        <div class="flex items-start gap-3">
          <AlertTriangle class="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 class="text-sm font-medium text-amber-800 dark:text-amber-200">This action will override existing assignments</h4>
            <p class="text-sm text-amber-700 dark:text-amber-300 mt-1">
              All existing module access records for every employee will be deleted and replaced with the new 44-month schedule starting from <strong>January {{ currentYear }}</strong>.
            </p>
          </div>
        </div>
      </div>

      <!-- What will happen -->
      <div class="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
        <h4 class="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
          <Info class="h-4 w-4" />
          What will happen
        </h4>
        <ul class="text-sm text-blue-700 dark:text-blue-300 space-y-1 ml-6 list-disc">
          <li>All existing module access month assignments will be removed</li>
          <li>Each employee will receive 44 months of access</li>
          <li>Coverage: <strong>Jan {{ currentYear }}</strong> through <strong>{{ endMonthLabel }} {{ endYear }}</strong></li>
        </ul>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl">
        <div class="flex items-start gap-3">
          <AlertCircle class="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div>
            <h4 class="text-sm font-medium text-red-800 dark:text-red-200">Error</h4>
            <p class="text-sm text-red-700 dark:text-red-300 mt-1">{{ error }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer with action buttons -->
    <template #footer>
      <BaseButton
        text="Cancel"
        variant="secondary"
        :disabled="syncing"
        @click="handleClose"
      />
      <BaseButton
        text="Sync All"
        variant="warning"
        :icon="RefreshCw"
        :loading="syncing"
        loading-text="Syncing..."
        @click="handleSync"
      />
    </template>
  </BaseModal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { RefreshCw, AlertTriangle, AlertCircle, Info } from 'lucide-vue-next'
import BaseModal from '@/components/common/BaseModal.vue'
import BaseButton from '@/components/common/BaseButton.vue'
import { employeeService } from '@/services/user'

interface Props {
  isVisible: boolean
}

interface Emits {
  close: []
  success: [message: string]
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const currentYear = new Date().getFullYear()

// Calculate end coverage: 44 months from January of current year
const endMonth = ((44 - 1) % 12) + 1 // Month 8 (August)
const endYear = currentYear + Math.floor((44 - 1) / 12) // +3 years

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const endMonthLabel = computed(() => monthNames[endMonth - 1])

const syncing = ref(false)
const error = ref<string | null>(null)

const handleClose = () => {
  if (!syncing.value) {
    error.value = null
    emit('close')
  }
}

const handleSync = async () => {
  if (syncing.value) return

  try {
    syncing.value = true
    error.value = null

    const response = await employeeService.syncModuleAccess()
    emit('success', response.message || 'Module access synced successfully')
    emit('close')
  } catch (err: any) {
    error.value = err.message || 'Failed to sync module access. Please try again.'
  } finally {
    syncing.value = false
  }
}
</script>
